// External dependencies
import { Sparkles, LucideIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";

// Internal components
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";

/**
 * Props interface for SidebarMenuWrapper component
 * @interface Props
 * @property {Object} item - Navigation item data
 * @property {string} item.title - Title of the navigation item
 * @property {string} item.url - URL for the navigation item
 * @property {LucideIcon} item.icon - Icon component for the navigation item
 * @property {Object[]} [item.items] - Optional sub-items for the navigation item
 * @property {string} item.items[].title - Title of the sub-item
 * @property {string} item.items[].url - URL for the sub-item
 * @property {boolean} item.items[].isActive - Whether the sub-item is active
 */
type Props = {
  item: {
    title: string;
    url: string;
    icon: LucideIcon;
    items?: {
      title: string;
      url: string;
      isActive: boolean;
    }[];
    glow?: boolean;
    isActive: boolean;
  };
};

/**
 * SidebarMenuWrapper Component
 *
 * Wrapper for sidebar menu items with collapsible functionality.
 * Handles different display modes (expanded vs collapsed) and hover cards.
 *
 * @param {Props} props - Component props
 */
export function SidebarMenuWrapper({ item }: Props) {
  // Only render a top-level nav button, no submenu/arrow logic
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={item.isActive}
        tooltip={item.title}
        className="cursor-pointer"
      >
        <Link to={item.url} aria-label={item.title}>
          <item.icon aria-hidden="true" />
          <span className="text-sm flex items-center gap-1">
            {item.title}
            {item.glow && (
              <Sparkles
                className="h-4 w-4 text-blue-400 drop-shadow-[0_0_8px_rgba(56,189,248,0.7)] animate-pulse pointer-events-none"
                aria-hidden="true"
                strokeWidth={2.5}
                fill="currentColor"
              />
            )}
          </span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}